import React, { useEffect, useRef, useState, useCallback } from 'react';
import { SearchIcon, ClearIcon, EnterIcon } from '../icons';
import { DEMO_TYPE_INPUT, DEMO_CLEAR_INPUT } from '../demo-transport';

// The one dropdownAnchor value that changes anything (item #17166). Named
// rather than spelled inline because the widget and the ingest boundary have to
// agree on the literal, and a bare string in a comparison is where they would
// stop agreeing without anything failing -- an unrecognised value renders the
// default, so the mode would simply stop working.
export const ANCHOR_INPUT = 'input';

export const SearchInput = ({
  query,
  onQueryChange,
  onClear,
  autoFocus,
  primaryColor,
  inputIconColor,
  placeholder = 'Search...',
  demoMode = false,
  // The demo's own event channel (item #8838). Null outside a demo, and null
  // for a demo host that has not been handed one -- either way this input
  // types only for the visitor.
  demoTransport = null,
  onFocus,
  // Optional in-input submit button (item #16450). Default off, so an instance
  // that configures nothing renders exactly what it rendered before.
  // The trailing clear control (item #17160). Default ON, the opposite of the
  // submit button below and for the same reason read the other way: this is
  // what every live instance renders today.
  showInputIcon = true,
  showClearButton = true,
  submitButtonEnabled = false,
  submitButtonLabel = '',
  submitButtonIcon = '',
  onSubmit,
  // Anything a host wants positioned against the BAR rather than against
  // whatever ancestor happens to be positioned (item #17191). The attached
  // surface passes its results panel; every other host passes nothing and
  // renders exactly what it rendered before.
  //
  // A slot rather than a prop the input interprets: this component does not
  // know what a dropdown is, and the one thing it can offer that its hosts
  // cannot arrange from outside is a place INSIDE its own root. The root is the
  // anchor -- INLINE_DROPDOWN_CSS declares it -- so a child of it positions
  // against the bar by construction rather than by three elements happening to
  // share width: 100%.
  children,
  // Which of this bar's two positioned boxes the slot above renders into
  // (item #17166): the root, which spans the input AND the submit button, or
  // the input's own field box, which stops at the button's leading edge the way
  // the reference bar does.
  //
  // Only an explicit ANCHOR_INPUT moves it. That is the compatibility floor
  // read literally rather than a lax comparison: a row predating the column, an
  // empty string, and a value the boundary should have refused all render what
  // they rendered before, on the bar.
  //
  // Both destinations already establish a containing block -- the root through
  // INLINE_DROPDOWN_CSS (item #17191), the field box through SEARCH_INPUT_CSS,
  // where it holds the magnifier and the clear button -- so this chooses
  // between two declared anchors rather than inventing a positioning scheme.
  dropdownAnchor = ''
}) => {
  const inputRef = useRef(null);
  const [localQuery, setLocalQuery] = useState(query);

  // Keep local query in sync with parent state
  useEffect(() => {
    setLocalQuery(query || '');
  }, [query]);

  // Auto-focus when enabled
  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  // The latest onQueryChange, read at delivery time rather than captured in the
  // subscription. An in-place host supplies it as an inline arrow, so a new
  // identity arrives on every render; depending on it here would rebuild the
  // subscription on every keystroke (item #8990).
  const onQueryChangeRef = useRef(onQueryChange);
  onQueryChangeRef.current = onQueryChange;

  // Handle demo events. These arrive on the transport this widget was given,
  // never on window, so autoplay in one demo cannot type into another (#8838).
  //
  // The subscription lives exactly as long as this input is mounted, because
  // that is what it means: autoplay's surface gate (#8842) reads the transport's
  // subscriber set to decide whether a surface exists to type into, and answers
  // a loss with stop() and a gain with start(), which begins a WHOLE query. A
  // subscription that churned per keystroke therefore restarted the script per
  // keystroke, and an in-place demo never got past its first character (#8990).
  useEffect(() => {
    if (!demoMode || !demoTransport) return;

    const handleTypeInput = (detail) => {
      if (inputRef.current) {
        const text = detail?.text ?? '';
        setLocalQuery(text);
        onQueryChangeRef.current(text);
      }
    };

    const handleClearInput = () => {
      if (inputRef.current) {
        setLocalQuery('');
        onQueryChangeRef.current('');
      }
    };

    const offType = demoTransport.on(DEMO_TYPE_INPUT, handleTypeInput);
    const offClear = demoTransport.on(DEMO_CLEAR_INPUT, handleClearInput);

    return () => {
      offType();
      offClear();
    };
  }, [demoMode, demoTransport]);

  // Handle input changes
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setLocalQuery(newValue);
    onQueryChange(newValue);
  };

  // Clear button handler
  const handleClear = () => {
    if (demoMode && typeof window !== 'undefined') {
      // User clicked reset button - stop demo and clear
      window.dispatchEvent(new CustomEvent('vo-search-user-focus'));
    }
    onClear();
  };

  // Submit handler. This is a THIRD path into the attached surface's open
  // state, alongside focusing the bar and typing, and item #16443 gated both of
  // those on having something to show. The gate is the host's to apply -- this
  // component does not know what a dropdown is -- so all that happens here is
  // that the intent is reported. Guarded because the modal and tile hosts
  // render the same input and pass no handler.
  const handleSubmit = useCallback(() => {
    if (demoMode && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('vo-search-user-focus'));
    }
    onSubmit?.();
  }, [demoMode, onSubmit]);

  // Enter is the second way to submit, and the one a visitor reaches for
  // (item #17172). It goes through handleSubmit rather than duplicating it, so
  // the button and the key report the same intent to the same host.
  //
  // Deliberately NOT gated on submitButtonEnabled: the button is chrome, and
  // submitting is the behaviour. An instance may want the key without the
  // button, and the host still decides what the intent means -- this component
  // does not know whether there is anywhere to go.
  //
  // preventDefault only on Enter, and only because a bare input inside a form
  // would otherwise submit the form as well. Every other key falls through
  // untouched; the caret keys in particular belong to the input.
  const handleKeyDown = useCallback((e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    handleSubmit();
  }, [handleSubmit]);

  // Handle user interactions that should stop demo
  const handleUserFocus = useCallback(() => {
    if (demoMode && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('vo-search-user-focus'));
    }
    onFocus?.();
  }, [demoMode, onFocus]);

  // Handle user clicks (works even when already focused)
  const handleUserClick = useCallback(() => {
    if (demoMode && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('vo-search-user-focus'));
    }
  }, [demoMode]);

  const anchoredToInput = dropdownAnchor === ANCHOR_INPUT;

  // Use inputIconColor for icons, fallback to primaryColor, then default
  const defaultColor = '#7013ad';
  const iconColor = inputIconColor || primaryColor || defaultColor;

  // The input carries no inline style object (item #16444). It used to declare
  // paddingLeft, width, height, border, outline, fontSize and borderRadius, and
  // an inline style beats every rule in SEARCH_INPUT_CSS -- whose selectors sit
  // at specificity 0 under :where() by design (decision #5090), so a component
  // that must compute a style at runtime still wins. None of those six values
  // had any runtime input, so the escape hatch was being spent on statics and
  // the curated block's own font-size was authored, shipped, and dead.
  //
  // width, border and outline moved nowhere: SEARCH_INPUT_CSS already declared
  // them identically. paddingLeft became the `padding` shorthand's fourth value
  // in both that block and vo-search.css, since the icon clearance is the
  // input's chrome rather than a computed one. borderRadius was dropped rather
  // than migrated -- the wrapper rounds AND clips (`overflow: hidden`), so the
  // input's own radius painted nothing. height was dropped for a reason worth
  // stating: under the reset's border-box, `height: 40px` with `padding: 12px 0`
  // leaves a 16px content box, which is why the old inline 16px font fit and the
  // block's 18px could not. The padding sizes the box now, and item #16445 makes
  // that tunable.
  return (
    <div className="vo-search-search-header">
      { /* Only the custom property is inline (item #17984). The wrapper used to
           carry borderColor, position, display, alignItems and width here as
           well -- five statics SEARCH_INPUT_CSS declares verbatim -- and an
           inline style beats every stylesheet, the instance's own (item #17981)
           included, so no customer rule on this element could ever compute.
           #16444's defect one element up. */ }
      <div
        className="vo-search-search-input-wrapper"
        style={{ '--primary-color': primaryColor }}
      >
        { /* The field box (item #16450). The magnifier and the clear button are
             absolutely positioned, and they used to resolve against the wrapper.
             With a submit button as a flex sibling that would put the clear
             control on top of it, so both now resolve against this box instead.
             With no submit button the box spans the whole wrapper, which is the
             same containing block they had before -- so an instance that
             configures nothing renders identically. The clear button itself is
             untouched: its own toggle is item #17160. */ }
        <div className="vo-search-search-input-field">
          { /* The leading magnifier (item #16449). Removing it also has to
               release the input's leading padding, which exists only as its
               clearance -- index.jsx emits
               --vo-search-input-leading-padding for exactly this case. The
               glyph and its clearance are one decision, so they are one
               item and one toggle. */ }
          {showInputIcon && (
            <div
              style={{
                position: 'absolute',
                left: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none',
                zIndex: 2,
                color: iconColor
              }}
            >
              <SearchIcon size={18} />
            </div>
          )}

          <input
            ref={inputRef}
            type="text"
            className="vo-search-search-input"
            placeholder={placeholder}
            value={localQuery}
            onChange={handleInputChange}
            onFocus={handleUserFocus}
            onClick={handleUserClick}
            onKeyDown={handleKeyDown}
          />

          { /* Two independent conditions, composed rather than merged (item
               #17160). The query gate is older than the toggle and asks a
               different question -- an empty box has nothing to clear -- so
               enabling the control must not make it appear before there is a
               query, and disabling it must not change what an empty box does. */ }
          {showClearButton && localQuery && (
            <button
              type="button"
              className="vo-search-search-clear"
              onClick={handleClear}
              aria-label="Clear search"
              style={{
                position: 'absolute',
                right: '10px',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: iconColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '24px',
                height: '24px',
                padding: 0
              }}
            >
              <ClearIcon size={16} />
            </button>
          )}
          {anchoredToInput && children}
        </div>

        { /* Styled entirely by SEARCH_INPUT_CSS and vo-search.css, with no
             inline style object. That is not tidiness: an inline object beats
             every rule in the curated block, whose selectors sit at specificity
             0 under :where() by design (decision #5090), and the fill this
             button exists to consume arrives as a custom property THROUGH that
             block. Declaring anything here would be item #16444's defect
             rebuilt on the one element that cannot afford it. */ }
        {submitButtonEnabled && (
          <button
            type="button"
            className="vo-search-search-submit"
            onClick={handleSubmit}
          >
            <span className="vo-search-search-submit-label">
              {submitButtonLabel || 'Search'}
            </span>
            {submitButtonIcon === 'enter' && <EnterIcon size={14} />}
          </button>
        )}
      </div>
      {!anchoredToInput && children}
    </div>
  );
};